import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, Network, History, Menu, X, LogOut, 
  ChevronDown, GraduationCap, FolderOpen, ArrowLeftCircle, Eye, User
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navigation = [
  { name: 'Mis Cursos', href: '/portal/dashboard', icon: LayoutDashboard },
  { name: 'Mi Perfil', href: '/portal/perfil', icon: User },
  { name: 'Repositorio', href: '/portal/repositorio', icon: FolderOpen },
  { name: 'Organigrama', href: '/portal/organigrama', icon: Network },
  { name: 'Mi Historial', href: '/portal/historial', icon: History },
];

export const PortalLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoImage, setLogoImage] = useState(null);
  const { user, token, logout, impersonating, exitImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/configuracion`);
      if (res.data.logo_image) {
        let imgUrl = res.data.logo_image;
        if (imgUrl.startsWith('/api/uploads/')) {
          imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
        }
        setLogoImage(imgUrl);
      }
    } catch (e) {
      console.log('Config not available');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleExitImpersonation = () => {
    exitImpersonation();
    navigate('/personas');
  };

  const getInitials = () => {
    if (!user) return '??';
    return `${(user.nombre || '')[0]}${(user.apellido || '')[0]}`.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="fixed top-0 left-0 right-0 bg-teal-500 text-white px-4 py-1.5 flex items-center justify-between z-[100]" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 text-sm">
            <Eye className="w-4 h-4" />
            <span className="font-medium">Viendo como:</span>
            <span>{user?.nombre} {user?.apellido}</span>
          </div>
          <button
            onClick={handleExitImpersonation}
            className="flex items-center gap-2 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-colors"
            data-testid="exit-impersonation-btn"
          >
            <ArrowLeftCircle className="w-4 h-4" />
            Volver al Admin
          </button>
        </div>
      )}
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 ${impersonating ? 'top-[36px]' : 'top-0'} h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo Header */}
        <div className="h-16 border-b border-slate-100 flex items-center px-2">
          <Link to="/portal/dashboard" className="flex-1 flex items-center justify-center">
            {logoImage ? (
              <div className="w-[232px] h-[56px] overflow-hidden flex items-center justify-center">
                <img
                  src={logoImage}
                  alt="Logo"
                  className="max-w-full max-h-full object-contain"
                  onError={() => setLogoImage(null)}
                  data-testid="portal-sidebar-logo"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="font-bold text-lg text-slate-900 tracking-tight">Portal</span>
              </div>
            )}
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`portal-nav-${item.href.split('/').pop()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive 
                    ? 'bg-teal-50 text-teal-600 border-l-4 border-teal-500' 
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'text-teal-500' : 'text-slate-400'}`} />
                <span className="flex-1">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 min-h-screen flex flex-col">
        {/* Header */}
        <header className={`sticky ${impersonating ? 'top-[36px]' : 'top-0'} z-30 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6`}>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
              data-testid="portal-menu-toggle"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            
            <div>
              <h1 className="text-lg font-bold text-slate-900">Portal del Trabajador</h1>
              <p className="text-xs text-slate-500">Gestión Organizacional</p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="portal-user-menu">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-white">{getInitials()}</span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-slate-700">
                    {user?.nombre} {user?.apellido}
                  </p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-slate-600">
                <span className="text-xs uppercase tracking-wider text-slate-400">Rol:</span>
                <span className="ml-2">Trabajador</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="portal-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>

        {/* Footer */}
        <footer className="py-4 px-6 text-center text-sm text-slate-400 border-t border-slate-200">
          © TystaCore {new Date().getFullYear()}
        </footer>
      </div>
    </div>
  );
};
